import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  getBottomBoundary,
  getLeftBoundary,
  getRightBoundary,
  getShapeChromeScreenRect,
  getTopBoundary,
} from '@/lib/editor/floating-canvas-chrome-bounds'
import {
  CROP_TOOLBAR_GAP,
  resolveCropToolbarPlacement,
} from '@/components/app/editor/tools/crop-toolbar'

/** Figma Photo main popover — 48px tall, width hugs all 8 controls. */
const TOOLBAR_HEIGHT = 48

/**
 * Places the toolbar on the same side of the shape the crop toolbar would take, `toolbarWidth`
 * being its measured width.
 */
export function computePhotoMainToolbarPos(
  shape: ShapeData,
  scale: number,
  canvasRect: DOMRect,
  toolbarWidth: number
): { x: number; y: number } {
  const {
    top: st,
    bottom: sb,
    left: sl,
    right: sr,
  } = getShapeChromeScreenRect(shape, scale, canvasRect)
  const shapeCenterX = (sl + sr) / 2
  const topBoundary = getTopBoundary()
  const bottomBoundary = getBottomBoundary()
  const leftBoundary = getLeftBoundary()
  const rightBoundary = getRightBoundary()

  const clampX = (x: number) =>
    Math.max(leftBoundary + 8, Math.min(rightBoundary - toolbarWidth - 8, x))

  const placement = resolveCropToolbarPlacement(shape, scale, canvasRect)

  if (placement === 'above') {
    return {
      x: clampX(shapeCenterX - toolbarWidth / 2),
      y: Math.max(topBoundary, st - TOOLBAR_HEIGHT - CROP_TOOLBAR_GAP),
    }
  }

  if (placement === 'below') {
    return {
      x: clampX(shapeCenterX - toolbarWidth / 2),
      y: Math.min(bottomBoundary - TOOLBAR_HEIGHT - 8, sb + CROP_TOOLBAR_GAP),
    }
  }

  if (placement === 'left') {
    return {
      x: Math.max(leftBoundary + 8, sl - toolbarWidth - CROP_TOOLBAR_GAP),
      y: Math.max(topBoundary, Math.min(bottomBoundary - TOOLBAR_HEIGHT - 8, st)),
    }
  }

  return {
    x: Math.min(rightBoundary - toolbarWidth - 8, sr + CROP_TOOLBAR_GAP),
    y: Math.max(topBoundary, Math.min(bottomBoundary - TOOLBAR_HEIGHT - 8, st)),
  }
}
